/** Pure export helpers: a re-importable project bundle, a written row-by-row
 *  pattern, and a PNG renderer. Kept free of React so they're unit-testable. */
import type { CellMap, PatternRecord } from './types';
import { isDark, stitchById, weightById, type Yarn } from './catalogs';

const key = (r: number, c: number) => `${r}-${c}`;

/** Row-by-row written instructions, read bottom-up, RS/WS alternating,
 *  run-length encoded by colour + stitch. */
export function writtenPattern(
  cells: CellMap,
  cols: number,
  rows: number,
  gaugeId: string,
  yarn: (id?: string | null) => Yarn | undefined
): string {
  const w = weightById(gaugeId) ?? weightById('dk')!;
  const used = new Set<string>();
  for (const cell of Object.values(cells)) if (cell.color) used.add(cell.color);

  let out = `SKEIN — ${cols} × ${rows} colourwork swatch\nGauge: ${w.name} (${w.desc})\n`;
  if (used.size) out += `Yarns: ${[...used].map((id) => yarn(id)?.name ?? id).join(', ')}\n`;
  out += `Read each row bottom-up; RS rows right→left, WS rows left→right.\n${'─'.repeat(40)}\n`;

  const rowW = String(rows).length;
  for (let i = 0; i < rows; i++) {
    const rowIdx = rows - 1 - i;
    const rs = i % 2 === 0;
    const order = rs ? [...Array(cols).keys()].reverse() : [...Array(cols).keys()];
    const seq = order.map((c) => {
      const d = cells[key(rowIdx, c)] ?? {};
      return { color: d.color ?? null, stitch: d.stitch ?? 'knit' };
    });
    const segs: { color: string | null; stitch: string; n: number }[] = [];
    let run = { ...seq[0], n: 1 };
    for (let k = 1; k < seq.length; k++) {
      if (seq[k].color === run.color && seq[k].stitch === run.stitch) run.n++;
      else {
        segs.push(run);
        run = { ...seq[k], n: 1 };
      }
    }
    segs.push(run);
    const txt = segs
      .map((s) => `${s.n} ${stitchById(s.stitch)?.short ?? 'K'} ${s.color ? (yarn(s.color)?.name ?? s.color) : 'ground'}`)
      .join(', ');
    out += `Row ${String(i + 1).padStart(rowW, ' ')} (${rs ? 'RS' : 'WS'}): ${txt}\n`;
  }
  return out;
}

/** Render the chart to a canvas at `scale` px/stitch. Caller supplies the canvas. */
export function drawChartPNG(
  canvas: HTMLCanvasElement,
  cells: CellMap,
  cols: number,
  rows: number,
  yarn: (id?: string | null) => Yarn | undefined,
  scale = 18
): HTMLCanvasElement {
  const pad = Math.round(scale * 0.2);
  canvas.width = cols * scale + pad * 2;
  canvas.height = rows * scale + pad * 2;
  const x = canvas.getContext('2d')!;
  x.fillStyle = '#1d1811';
  x.fillRect(0, 0, canvas.width, canvas.height);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const d = cells[key(r, c)];
      const px = pad + c * scale;
      const py = pad + r * scale;
      const y = d?.color ? yarn(d.color) : undefined;
      x.fillStyle = y ? y.hex : '#251e16';
      x.fillRect(px, py, scale, scale);
      const st = d?.stitch ? stitchById(d.stitch) : undefined;
      if (st?.sym) {
        x.fillStyle = y && isDark(y.hex) ? 'rgba(255,255,255,.85)' : 'rgba(20,14,4,.8)';
        x.font = `700 ${Math.round(scale * 0.6)}px ui-monospace, monospace`;
        x.textAlign = 'center';
        x.textBaseline = 'middle';
        x.fillText(st.sym, px + scale / 2, py + scale / 2 + 1);
      }
    }
  }
  const W = cols * scale;
  const H = rows * scale;
  x.strokeStyle = 'rgba(0,0,0,.35)';
  x.lineWidth = 1;
  for (let i = 0; i <= cols; i++) line(x, pad + i * scale + 0.5, pad, pad + i * scale + 0.5, pad + H);
  for (let i = 0; i <= rows; i++) line(x, pad, pad + i * scale + 0.5, pad + W, pad + i * scale + 0.5);
  x.strokeStyle = 'rgba(232,169,78,.45)';
  x.lineWidth = 1.5;
  for (let i = 0; i <= cols; i += 5) line(x, pad + i * scale + 0.5, pad, pad + i * scale + 0.5, pad + H);
  for (let i = 0; i <= rows; i += 5) line(x, pad, pad + i * scale + 0.5, pad + W, pad + i * scale + 0.5);
  return canvas;
}
function line(x: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number) {
  x.beginPath();
  x.moveTo(x1, y1);
  x.lineTo(x2, y2);
  x.stroke();
}

/** Re-importable project JSON for a single pattern (matches the legacy shape). */
export function projectJSON(rec: PatternRecord) {
  const grid: (object | null)[][] = [];
  for (let r = 0; r < rec.rows; r++) {
    const row: (object | null)[] = [];
    for (let c = 0; c < rec.cols; c++) row.push(rec.cells[key(r, c)] ? { ...rec.cells[key(r, c)] } : null);
    grid.push(row);
  }
  return {
    meta: { app: 'SKEIN', version: 3, name: rec.name, cols: rec.cols, rows: rec.rows, gauge: rec.gauge, exported: new Date().toISOString() },
    palette: rec.palette,
    grid,
  };
}

export function slug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'skein-chart';
}
